import { useState, useEffect, useCallback, useRef } from 'react';
import { useAdminAuth } from '@/contexts/AdminAuthContext';
import { supabase } from '@/integrations/supabase/client';
import AdminLayout from '@/components/admin/AdminLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { 
  Users, 
  FileText, 
  TrendingUp, 
  Clock, 
  AlertTriangle, 
  Building2,
  UserCog,
  Activity,
  CreditCard,
  RotateCcw
} from 'lucide-react';
import ChartThemeToggle from '@/components/dashboard/ChartThemeToggle';
import RoleSwitcher from '@/components/admin/RoleSwitcher';
import WelcomeBanner from '@/components/dashboard/WelcomeBanner';
import QuickStatsWidget, { StatItem } from '@/components/dashboard/QuickStatsWidget';
import CostSavingsSummaryWidget from '@/components/admin/CostSavingsSummaryWidget';
import BillingBasedSavingsWidget from '@/components/admin/BillingBasedSavingsWidget';
import PatientRiskTrendChart from '@/components/admin/PatientRiskTrendChart';
import CumulativeBillableHoursChart from '@/components/admin/CumulativeBillableHoursChart';
import CareUtilizationMixChart from '@/components/admin/CareUtilizationMixChart';
import NotificationCenter from '@/components/dashboard/NotificationCenter';
import GoalStatusWidget from '@/components/dashboard/GoalStatusWidget';
import DateRangeFilter from '@/components/dashboard/DateRangeFilter';
import DashboardExport from '@/components/dashboard/DashboardExport';
import { AgencyNotificationBell } from '@/components/admin/AgencyNotificationBell';
import { useDashboardTrends } from '@/hooks/useTrendCalculator';
import { usePersistedTrendPeriod } from '@/hooks/usePersistedTrendPeriod';
import { usePersistedDateRange } from '@/hooks/usePersistedDateRange';
import { useGoalThresholds } from '@/hooks/useGoalThresholds';

interface DashboardStats {
  totalPatients: number;
  activePatients: number;
  totalSessions: number;
  completedSessions: number;
  recentSessions: number;
  avgResponseTime: number;
  redFlagCases: number;
  totalSupervisors: number;
}

export default function AgencyDashboardPage() {
  const { user } = useAdminAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [stats, setStats] = useState<DashboardStats>({
    totalPatients: 0,
    activePatients: 0,
    totalSessions: 0,
    completedSessions: 0,
    recentSessions: 0,
    avgResponseTime: 0,
    redFlagCases: 0,
    totalSupervisors: 0
  });
  const [loading, setLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);
  const [agencyName, setAgencyName] = useState<string>('');
  const [trendPeriod, setTrendPeriod] = usePersistedTrendPeriod('week');
  const [dateRange, setDateRange] = usePersistedDateRange();
  const analyticsContainerRef = useRef<HTMLDivElement>(null);
  const costSavingsRef = useRef<HTMLDivElement>(null);
  const billingRef = useRef<HTMLDivElement>(null);
  const riskTrendRef = useRef<HTMLDivElement>(null);
  const billableHoursRef = useRef<HTMLDivElement>(null);
  const careUtilizationRef = useRef<HTMLDivElement>(null);

  // Check for impersonation mode (super admin viewing as agency)
  const isImpersonating = searchParams.get('impersonate') === 'agency';
  const impersonatedAgencyId = searchParams.get('aid');
  const effectiveAgencyId = isImpersonating ? impersonatedAgencyId : user?.agency_id;
  
  // Get trends for this agency
  const { trends } = useDashboardTrends({ 
    agencyId: effectiveAgencyId || undefined, 
    period: trendPeriod 
  });
  const { thresholds } = useGoalThresholds(effectiveAgencyId || undefined, trendPeriod);
  
  const hasActiveFilters = dateRange.from !== undefined || dateRange.to !== undefined || trendPeriod !== 'week';
  
  const handleResetFilters = useCallback(() => {
    setDateRange({ from: undefined, to: undefined });
    setTrendPeriod('week');
  }, [setDateRange, setTrendPeriod]);

  const triggerUpdateAnimation = () => {
    setIsUpdating(true);
    setTimeout(() => setIsUpdating(false), 1500);
  };

  useEffect(() => {
    if (effectiveAgencyId) {
      loadAgencyInfo();
      loadDashboardStats();

      const channel = supabase
        .channel('agency-dashboard-realtime')
        .on('postgres_changes', { event: '*', schema: 'public', table: 'triage_sessions' }, () => {
          triggerUpdateAnimation();
          loadDashboardStats();
        })
        .on('postgres_changes', { event: '*', schema: 'public', table: 'patients' }, () => {
          triggerUpdateAnimation();
          loadDashboardStats();
        })
        .subscribe();

      return () => {
        supabase.removeChannel(channel);
      };
    }
  }, [effectiveAgencyId]);

  const loadAgencyInfo = async () => {
    if (!effectiveAgencyId) return;

    try {
      const { data } = await supabase
        .from('agencies')
        .select('name')
        .eq('id', effectiveAgencyId)
        .single();

      if (data) {
        setAgencyName(data.name);
      }
    } catch (error) {
      console.error('Error loading agency info:', error);
    }
  };

  const loadDashboardStats = async () => {
    if (!effectiveAgencyId) {
      setLoading(false);
      return;
    }

    try {
      const [
        { count: totalPatients },
        { count: activePatients },
        { count: totalSessions },
        { count: completedSessions },
        { count: redFlagCases }
      ] = await Promise.all([
        supabase.from('patients').select('*', { count: 'exact', head: true }).eq('agency_id', effectiveAgencyId),
        supabase.from('patients').select('*', { count: 'exact', head: true }).eq('agency_id', effectiveAgencyId).eq('is_active', true),
        supabase.from('triage_sessions').select('*', { count: 'exact', head: true }).eq('agency_id', effectiveAgencyId),
        supabase.from('triage_sessions').select('*', { count: 'exact', head: true }).eq('agency_id', effectiveAgencyId).not('completed_at', 'is', null),
        supabase.from('triage_sessions').select('*', { count: 'exact', head: true }).eq('agency_id', effectiveAgencyId).eq('red_flags', true)
      ]);

      // Count supervisors in this agency
      const { data: supervisorProfiles } = await supabase
        .from('profiles')
        .select('user_id')
        .eq('agency_id', effectiveAgencyId)
        .eq('is_active', true);

      let supervisorCount = 0;
      if (supervisorProfiles && supervisorProfiles.length > 0) {
        const userIds = supervisorProfiles.map(p => p.user_id);
        const { count } = await supabase
          .from('user_roles')
          .select('*', { count: 'exact', head: true })
          .in('user_id', userIds)
          .eq('role', 'supervisor');
        supervisorCount = count || 0;
      }

      // Recent sessions (last 7 days)
      const sevenDaysAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();
      const { count: recentSessions } = await supabase
        .from('triage_sessions')
        .select('*', { count: 'exact', head: true })
        .eq('agency_id', effectiveAgencyId)
        .gte('created_at', sevenDaysAgo);

      // Average response time
      const { data: completedForAvg } = await supabase
        .from('triage_sessions')
        .select('started_at, completed_at')
        .eq('agency_id', effectiveAgencyId)
        .not('completed_at', 'is', null)
        .limit(100);

      let avgResponseTime = 0;
      if (completedForAvg && completedForAvg.length > 0) {
        const totalMinutes = completedForAvg.reduce((sum, s) => {
          const start = new Date(s.started_at).getTime();
          const end = new Date(s.completed_at!).getTime();
          return sum + (end - start) / 60000;
        }, 0);
        avgResponseTime = Math.round((totalMinutes / completedForAvg.length) * 10) / 10;
      }

      setStats({
        totalPatients: totalPatients || 0,
        activePatients: activePatients || 0,
        totalSessions: totalSessions || 0,
        completedSessions: completedSessions || 0,
        recentSessions: recentSessions || 0,
        avgResponseTime,
        redFlagCases: redFlagCases || 0,
        totalSupervisors: supervisorCount
      });
    } catch (error) {
      console.error('Error loading dashboard stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const statCards: StatItem[] = [
    { 
      title: 'Total Patients', 
      value: stats.totalPatients, 
      description: `${stats.activePatients} active`, 
      icon: Users, 
      color: 'text-blue-600', 
      onClick: () => navigate('/admin/patients'),
      trend: trends.patients ? { 
        value: trends.patients.percentChange, 
        isPositive: trends.patients.isPositive, 
        label: trends.patients.label,
        sparklineData: trends.patients.sparklineData,
        threshold: thresholds.patients
      } : undefined
    },
    { 
      title: 'Supervisors', 
      value: stats.totalSupervisors, 
      description: 'Staff members', 
      icon: UserCog, 
      color: 'text-indigo-600', 
      onClick: () => navigate('/admin/supervisors') 
    },
    { 
      title: 'Triage Sessions', 
      value: stats.totalSessions, 
      description: `${stats.completedSessions} completed`, 
      icon: FileText, 
      color: 'text-green-600', 
      onClick: () => navigate('/admin/analytics'),
      trend: trends.sessions ? { 
        value: trends.sessions.percentChange, 
        isPositive: trends.sessions.isPositive, 
        label: trends.sessions.label,
        sparklineData: trends.sessions.sparklineData,
        threshold: thresholds.sessions
      } : undefined
    },
    { 
      title: 'Recent Activity', 
      value: stats.recentSessions, 
      description: 'Last 7 days', 
      icon: TrendingUp, 
      color: 'text-purple-600', 
      onClick: () => navigate('/admin/analytics'),
      trend: trends.recentActivity ? { 
        value: trends.recentActivity.percentChange, 
        isPositive: trends.recentActivity.isPositive, 
        label: trends.recentActivity.label,
        sparklineData: trends.recentActivity.sparklineData,
        threshold: thresholds.sessions
      } : undefined
    },
    { 
      title: 'Avg Response Time', 
      value: `${stats.avgResponseTime.toFixed(1)}min`, 
      description: 'Per assessment', 
      icon: Clock, 
      color: 'text-orange-600', 
      onClick: () => navigate('/admin/analytics') 
    },
    { 
      title: 'Red Flag Cases', 
      value: stats.redFlagCases, 
      description: 'Require attention', 
      icon: AlertTriangle, 
      color: 'text-red-600', 
      onClick: () => navigate('/admin/patients?status=red_flags'),
      trend: trends.redFlags ? { 
        value: trends.redFlags.percentChange, 
        isPositive: trends.redFlags.isPositive, 
        label: trends.redFlags.label,
        sparklineData: trends.redFlags.sparklineData,
        threshold: thresholds.redFlags
      } : undefined
    },
  ];

  if (loading) {
    return (
      <AdminLayout>
        <div className="space-y-8">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-muted rounded w-1/4"></div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="h-32 bg-muted rounded-lg"></div>
              ))}
            </div>
          </div>
        </div>
      </AdminLayout>
    );
  }

  if (!effectiveAgencyId) {
    return (
      <AdminLayout>
        <Card className="max-w-md mx-auto mt-8">
          <CardContent className="p-8 text-center space-y-4">
            <AlertTriangle className="h-12 w-12 text-warning mx-auto" />
            <h2 className="text-2xl font-bold">No Agency Associated</h2>
            <p className="text-muted-foreground">
              Your account is not associated with any agency. Please contact an administrator.
            </p>
          </CardContent>
        </Card>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-8">
        {/* Welcome Banner */}
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
          <div className="flex-1">
            <WelcomeBanner
              role="agency_admin"
              userName={user?.email?.split('@')[0] || 'Admin'}
              userEmail={user?.email}
              agencyName={agencyName}
              quickActions={[
                { label: 'Manage Patients', icon: Users, onClick: () => navigate('/admin/patients') },
                { label: 'Supervisors', icon: UserCog, onClick: () => navigate('/admin/supervisors'), variant: 'outline' },
                { label: 'Billing', icon: CreditCard, onClick: () => navigate('/admin/billing'), variant: 'outline' },
              ]}
            >
              {isUpdating && (
                <div className="flex items-center gap-2 text-success text-sm">
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-success opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-success"></span>
                  </span>
                  Live update detected
                </div>
              )}
            </WelcomeBanner>
          </div>
          <div className="flex items-center gap-2">
            <AgencyNotificationBell />
            <RoleSwitcher currentView="agency_admin" />
          </div>
        </div>

        {/* Agency Overview Badge */}
        <div className="flex items-center gap-4 p-4 bg-gradient-to-r from-blue-500/5 to-indigo-500/5 rounded-lg border border-blue-500/10">
          <div className="p-3 rounded-full bg-blue-500/10">
            <Building2 className="h-6 w-6 text-blue-600" />
          </div>
          <div>
            <h3 className="font-semibold text-foreground">{agencyName || 'Agency'} Overview</h3>
            <p className="text-sm text-muted-foreground">
              {stats.totalPatients} patients • {stats.totalSupervisors} supervisors • {stats.totalSessions} total sessions
            </p>
          </div>
          <Badge variant="outline" className="ml-auto hidden sm:flex">
            <Activity className="h-3 w-3 mr-1" />
            Agency Scope
          </Badge>
        </div>

        {/* Stats Grid */}
        <QuickStatsWidget 
          stats={statCards} 
          columns={6} 
          isUpdating={isUpdating}
          trendPeriod={trendPeriod}
          onTrendPeriodChange={setTrendPeriod}
        />

        {/* Quick Actions */}
        <Card>
          <CardHeader>
            <CardTitle>Quick Actions</CardTitle>
            <CardDescription>Common tasks for agency management</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-3">
              <Button onClick={() => navigate('/admin/patients')}>
                <Users className="h-4 w-4 mr-2" />
                Manage Patients
              </Button>
              <Button variant="outline" onClick={() => navigate('/admin/supervisors')}>
                <UserCog className="h-4 w-4 mr-2" />
                Manage Supervisors
              </Button>
              <Button variant="outline" onClick={() => navigate('/admin/analytics')}>
                <TrendingUp className="h-4 w-4 mr-2" />
                View Analytics
              </Button>
              <Button variant="outline" onClick={() => navigate('/admin/billing')}>
                <FileText className="h-4 w-4 mr-2" />
                Billing & Invoices
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Analytics Widgets */}
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-foreground">Analytics Overview</h3>
          <div className="flex items-center gap-2">
            {hasActiveFilters && (
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={handleResetFilters}
                className="h-8 text-gray-500 hover:text-gray-700"
              >
                <RotateCcw className="h-4 w-4 mr-1" />
                Reset
              </Button>
            )}
            <ChartThemeToggle />
            <DateRangeFilter 
              dateRange={dateRange}
              onDateRangeChange={setDateRange}
            />
            <DashboardExport
              stats={stats}
              agencyName={agencyName}
              dateRange={dateRange}
              analyticsContainerRef={analyticsContainerRef}
              chartRefs={[
                { ref: costSavingsRef, title: 'Cost Savings Summary' },
                { ref: billingRef, title: 'Billing Based Savings' },
                { ref: riskTrendRef, title: 'Patient Risk Trend' },
                { ref: billableHoursRef, title: 'Cumulative Billable Hours' },
                { ref: careUtilizationRef, title: 'Care Utilization Mix' },
              ]}
            />
          </div>
        </div>

        <div ref={analyticsContainerRef} className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div ref={costSavingsRef}>
            <CostSavingsSummaryWidget 
              agencyId={effectiveAgencyId} 
              agencyName={agencyName}
              startDate={dateRange.from}
              endDate={dateRange.to}
            />
          </div>
          <div ref={billingRef}>
            <BillingBasedSavingsWidget 
              agencyId={effectiveAgencyId}
              agencyName={agencyName}
              startDate={dateRange.from}
              endDate={dateRange.to}
            />
          </div>
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div ref={riskTrendRef}>
            <PatientRiskTrendChart 
              agencyId={effectiveAgencyId}
              agencyName={agencyName}
              startDate={dateRange.from}
              endDate={dateRange.to}
            />
          </div>
          <GoalStatusWidget agencyId={effectiveAgencyId} />
          <NotificationCenter role="agency_admin" agencyId={effectiveAgencyId} />
        </div>

        {/* Billable Hours & Care Utilization Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div ref={billableHoursRef}>
            <CumulativeBillableHoursChart 
              agencyId={effectiveAgencyId}
              agencyName={agencyName}
              startDate={dateRange.from}
              endDate={dateRange.to}
            />
          </div>
          <div ref={careUtilizationRef}>
            <CareUtilizationMixChart 
              startDate={dateRange.from}
              endDate={dateRange.to}
              agencyId={effectiveAgencyId || undefined}
              agencyName={agencyName}
            />
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
